import React, { Suspense } from "react";
import MetadataSkeleton from "./metadata-skeleton/MetadataSkeleton";
import { baseUrl, getComponentFromPath } from "@kaltura/mediaspace-shared-utils";
import { KmsTypeComponent, KmsTypeHeroProps } from "@kaltura/mediaspace-shared-types";
import { Description } from "./description/Description";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { styled } from "@mui/material";
import { Box } from "@kaltura/mediaspace-shared-styled";
import { useFetchKmsData } from "@kaltura/mediaspace-shared-hooks";

interface MediaMetadata {
    components: KmsTypeComponent[];
    description: string;
}

interface MediaMetadataInnerProps {
    fetchMetadata: () => MediaMetadata;
    isEmptyRightPanel: boolean;
}

const StyledComponentContainer = styled(Box)({
    "&:empty": {
        display: "none",
    },
});

/**
 * Media Metadata - description, tags, etc.
 */
function MediaMetadata(props: MediaMetadataInnerProps) {
    const { fetchMetadata, isEmptyRightPanel } = props;
    const metadata = fetchMetadata();

    const className = "kms-ds-media-page-metadata";

    return (
        <div className={className}>
            {metadata.components
                .filter(({ order = 0 }) => order < 0)
                .map((component: KmsTypeComponent, index: number) => {
                    const Component = getComponentFromPath(component.component);
                    return (
                        <ErrorBoundary key={`before-${index}`} fallback={<></>}>
                            <StyledComponentContainer className={className + "-item"}>
                                <Component
                                    {...component.props}
                                    isEmptyRightPanel={isEmptyRightPanel}
                                    parent={"media-metadata"}
                                />
                            </StyledComponentContainer>
                        </ErrorBoundary>
                    );
                })}
            <div className={className + "-item"}>
                <Description description={metadata.description} />
            </div>
            {metadata.components
                .filter(({ order = 0 }) => order >= 0)
                .map((component: KmsTypeComponent, index: number) => {
                    const Component = getComponentFromPath(component.component);
                    return (
                        <ErrorBoundary key={`after-${index}`} fallback={<></>}>
                            <div className={className + "-item"}>
                                <Component {...component.props} />
                            </div>
                        </ErrorBoundary>
                    );
                })}
        </div>
    );
}

export interface MediaMetadataContainerProps extends Pick<KmsTypeHeroProps, "categoryId" | "playlist"> {
    entryId: string;
    isEmptyRightPanel: boolean;
    reloadCounter?: number;
    isReadOnly?: boolean;
}

/**
 * Media Metadata Suspense container
 */
export function MediaMetadataContainer(props: MediaMetadataContainerProps) {
    const { entryId, categoryId, playlist, isEmptyRightPanel, reloadCounter = 0, isReadOnly = false } = props;

    // fetch data from kms
    const url =
        baseUrl
        + "/default/media/metadata/id/"
        + entryId
        + (categoryId ? "/categoryid/" + categoryId : "")
        + (playlist ? "/playlistid/" + playlist.id : "");
    const fetchMetadata = useFetchKmsData<MediaMetadata>({ url, data: { isReadOnly } }, [url, reloadCounter]);

    // render with Suspense
    return (
        <ErrorBoundary fallback={<MetadataSkeleton />}>
            <Suspense fallback={<MetadataSkeleton />}>
                <MediaMetadata fetchMetadata={fetchMetadata} isEmptyRightPanel={isEmptyRightPanel} />
            </Suspense>
        </ErrorBoundary>
    );
}

export default MediaMetadataContainer;
